/**
 * Format a score for display
 * - Removes decimals (scores are always whole numbers)
 * - Adds commas for thousands separators
 * @param {number} score - The score to format
 * @returns {string} Formatted score (e.g., "1,234,567")
 */
export function formatScore(score) {
  if (score === null || score === undefined) {
    return '0';
  }

  // Convert to integer (remove decimals) and format with commas
  return Math.round(score).toLocaleString('en-US');
}

// Abbreviated score display (e.g. "18.4M", "1.23B") - for scores that routinely run into
// the millions/billions, where full-precision formatScore() would overflow or wrap awkwardly
// in tight UI (stat tiles, TV/roster rows, compact list rows). Originally built for the
// Transformers competition pages; kept separate from formatScore() above since other,
// unrelated features (e.g. the scorekeeping leaderboard) still want full-precision scores.
export function formatScoreAbbreviated(score) {
  if (score === null || score === undefined || Number.isNaN(Number(score))) return '0';
  const num = Math.round(Number(score));
  const abs = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1)}K`;
  return num.toLocaleString('en-US');
}

// Groups a raw digit string with thousands separators for display while someone is typing
// (e.g. "100000000" -> "100,000,000"). State keeps the raw digits and only the rendered value
// carries commas, so parseInt and every validation path stay unchanged. Uses string grouping
// rather than Number.toLocaleString so an arbitrarily long entry can't lose precision.
export function formatDigitGroups(rawDigits) {
  const digits = String(rawDigits ?? '').replace(/\D/g, '');
  if (!digits) return '';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
